import { useState } from "react";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Button, Divider, Typography } from "@mui/material";

import { TabsIndicators } from "../../../../../shared/UI/TabsIndicators";

import { Bucket } from "../../../enum/jobEnums";
import { addTalentReviewSchema } from "../../../schemas/createReviewSchema";
import { AddTalentReviewCandidate } from "../../../types/addReviewCandidate";

import OverviewStep from "./OverviewStep";
import TechnicalSkillsStep from "./TechnicalSkillsStep";
import TacticalAwarenessStep from "./TacticalAwarenessStep";
import FinalNarrativeTagsStep from "./FinalNarrativeTagsStep";
import AthleticismPhysicalStep from "./AthleticismPhysicalStep";
import MindsetPsychologicalStep from "./MindsetPsychologicalStep";

export const TalentReportSteps = ({
  bucket,
  onClose,
  handleAddCandidateReview,
}: {
  bucket: Bucket;
  onClose: () => void;
  handleAddCandidateReview: (data: AddTalentReviewCandidate) => void;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal.talentReport" });
  const [step, setStep] = useState(0);

  const createJobStepsMapper: Record<number, JSX.Element> = {
    0: <OverviewStep />,
    1: <TechnicalSkillsStep />,
    2: <TacticalAwarenessStep />,
    3: <AthleticismPhysicalStep />,
    4: <MindsetPsychologicalStep />,
    5: <FinalNarrativeTagsStep />,
  };
  const stepsLength = Object.keys(createJobStepsMapper).length;
  const nextButtonLabel = step === stepsLength - 1 ? t("addReview") : t("next");
  const previousButtonLabel = step === 0 ? t("cancel") : t("previous");
  const headerTitle: Record<number, string> = {
    0: t("quickReview"),
    1: t("technicalSkills"),
    2: t("tacticalAwareness"),
    3: t("athleticismPhysical"),
    4: t("mindsetPsychological"),
    5: t("finalNarrativeTags"),
  };

  const methods = useForm<AddTalentReviewCandidate>({
    resolver: zodResolver(addTalentReviewSchema),
    mode: "all",
    defaultValues: {
      whoIsReviewing: "ENTITY_ADMIN",
      overallFitScore: 8,
    },
  });

  const handleGoBack = () => {
    if (step === 0) {
      setStep(0);
      // onClose();
    } else {
      setStep((prev) => prev - 1);
    }
  };

  const handleGoToNextStep = async () => {
    let isValid = true;
    if (step === 0) {
      isValid = await methods.trigger(["whoIsReviewing", "overallFitScore", "talentAssessment"]);
    } else if (step === 1) {
      isValid = await methods.trigger([
        "firstTouchReceiving",
        "passingRangeAccuracy",
        "dribbling1v1Ability",
        "shootingFinishing",
        "crossingDelivery",
        "headingTechnique",
      ]);
    } else if (step === 2) {
      isValid = await methods.trigger([
        "positioning",
        "offTheBallMovement",
        "defensiveAwareness",
        "transitionPlay",
        "spatialAwareness",
        "tacticalAdaptability",
      ]);
    } else if (step === 3) {
      isValid = await methods.trigger([
        "acceleration",
        "topSpeed",
        "strength",
        "endurance",
        "agility",
        "flexibilityCoordination",
      ]);
    } else if (step === 4) {
      isValid = await methods.trigger([
        "concentration",
        "confidence",
        "leadership",
        "discipline",
        "workRate",
        "resilience",
        "coachAbility",
        "professionalism",
      ]);
    } else if (step === 5) {
      isValid = await methods.trigger(["ginoSummary", "tags"]);
    }

    if (!isValid) return;

    if (step === stepsLength - 1) {
      methods.handleSubmit(handleAddCandidateReview)();
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  return (
    <FormProvider {...methods}>
      <Box sx={[styles.containerPadding, styles.headerContainer]}>
        <Typography sx={styles.headerTitle}>{headerTitle[step]}</Typography>
        <TabsIndicators stepsLength={stepsLength} currentStep={step + 1} />
      </Box>
      <Divider flexItem orientation="horizontal" />
      {createJobStepsMapper[step]}
      <Divider flexItem orientation="horizontal" />
      <Box sx={[styles.buttonContainer, styles.containerPadding]}>
        <Button onClick={handleGoBack} variant="outlined-md">
          {previousButtonLabel}
        </Button>
        <Button onClick={handleGoToNextStep} variant="primary-md">
          {nextButtonLabel}
        </Button>
      </Box>
    </FormProvider>
  );
};

export default TalentReportSteps;

const styles = {
  containerPadding: {
    px: "24px",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  headerTitle: {
    fontFamily: "Montserrat",
    color: "var(--colors-text-text-primary-900, #101828)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    pb: "24px",
  },
};
